import { User } from '@/models/user'

export enum EvCarProfileStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  VERIFYING = 'verifying',
  INCOMPLETE = 'incomplete',
  ADMIN_BLOCKED = 'admin_blocked',
  ADMIN_ACTIVE = 'admin_active',
}

export enum ChargingConnectorType {
  TYPE_1 = 'Type 1 (J1772)',
  TYPE_2 = 'Type 2 (Mennekes)',
  CHADEMO = 'CHAdeMO',
  CCS = 'CCS (Combined Charging System)',
  TESLA = 'Tesla Supercharger',
}

export interface EvCarImage {
  id: string
  ev_car_profile_id: string
  public_id: string
  secure_url: string
  type: string
  createdAt: string
  updatedAt: string
}

export interface AdminComment {
  action:
    | 'blocked'
    | 'unblocked'
    | 'comment'
    | 'activated'
    | 'deactivated'
    | 'admin_activated'
    | 'admin_deactivated'
    | 'status_change'
  comment: string
  status_from: string
  status_to: string
  admin_id: string
  admin_email?: string
  created_at: string
}

export interface EvCarProfileFeatures {
  gps: boolean | null
  air_conditioner: boolean | null
  bluetooth_stereo: boolean | null
  wifi_internet: boolean | null
  fire_extinguisher: boolean | null
}

export interface EvCarDetailsModule {
  data: EvCarProfile | null
  updating: {
    successKeys: (keyof EvCarProfile)[]
    loadingKeys: (keyof EvCarProfile)[]
    error: string | null
  }
}

export interface EvCarsState {
  evCarsDetails: Record<string, EvCarDetailsModule>
  listModule: {
    list: EvCarProfile[]
    meta: {
      total: number
      perPage: number
      currentPage: number
      lastPage: number
    }
    isLoading: boolean
    error: string | null
  }
}

export interface EvCarProfileFull extends EvCarProfile {
  // user: User
  booking_status?: string
  // trips: TripWithRenter[]
  // airport_pickup_locations: CarPickupReturnLocation<PickupReturnAction.PICKUP>[]
  // airport_return_locations: CarPickupReturnLocation<PickupReturnAction.RETURN>[]
  // insurance_request: InsuranceRequestsReturn
}

interface LocationGeom {
  type: string
  coordinates: [number, number]
}

interface BatteryDetails {
  capacity?: number
  level?: number
  status?: string
  health?: number
  temperature?: number
  [key: string]: number | string | undefined
}

interface ChargingType {
  type: string
  power: number
  connector: string
  [key: string]: string | number
}

export interface EvCarProfile {
  id: string
  admin_id: string
  owner_ev_car: string | null
  place_id: string | null
  address: string | null

  model_make_id: string
  model_id: number | null
  model_name: string
  model_year: number

  rating: string

  battery_capacity: number
  charging_connector_type: ChargingConnectorType
  charging_time: number
  range_per_charge: number
  price_per_minute_ev: number
  price_per_hour_ev: number
  price_per_km_ev: number
  price_per_minute_up_to_100_km_ev: number
  price_per_minute_above_100_km_ev: number
  price_per_hour_up_to_100_km_ev: number
  price_per_hour_above_100_km_ev: number
  start_price: number
  reservation_extension_price: number
  reserve_price_ev: number
  pause_price_ev: number
  deposit_amount_ev: number

  car_type: string
  transmission: string
  fuel_type: string
  no_branded_title: boolean
  identity_country: string
  plate_number: string

  seats: number | null
  doors: number | null

  status: string
  car_description: string | null

  charging_included: boolean
  battery_included: boolean
  charging_policy: string | null

  features: EvCarProfileFeatures | null
  images: EvCarImage[]

  createdAt: string
  updatedAt: string
  status_doors: boolean
  status_engine: boolean
  status_windows: boolean
  type_connection_geotek: string
  activate_key_ev_car: boolean

  // Location and geolocation fields
  location: {
    lat: number
    lng: number
  }
  locality: string | null
  location_geom: LocationGeom | null

  // Battery and range related fields
  range_per_full_charge: number
  electric_level: number
  battery_details: BatteryDetails | null
  supported_charging_types: ChargingType[]

  // Vehicle status and metrics
  odometer_indicators: number
  remaining_distance: number
  engine_status: boolean

  // Reservation and limits
  max_distance_per_tariff: number
  max_distance_per_minute_tariff: number
  max_distance_per_hour_tariff: number
  reservation_timer: number
  reservation_status: string
  pause_limit: number
  pause_usage_limit: boolean

  // Additional car details
  no_smoking: boolean
  current_location: string
  registered_car: boolean
  external_id: string
  registration_status: string
  min_age_requirement: number
  advance_notice: string

  // Documents
  ownership_permit: Array<{
    type?: string
    public_id: string
    secure_url: string
  }>

  // Direct wifi property (currently in features)
  wifi_internet: boolean

  // Admin comments history
  admin_comment: AdminComment[]

  // Night renter assignment (in force between assigned_from and assigned_until)
  assigned_user_id: string | null
  assigned_from: string | null
  assigned_until: string | null
  assigned_user: EvCarAssignedUser | null
}

export type EvCarAssignedUser = Pick<
  User,
  'id' | 'first_name' | 'last_name' | 'phone'
>

export type EvCarProfileUpdateParams = Partial<
  Pick<
    EvCarProfile,
    | 'images'
    | 'place_id'
    | 'address'
    | 'model_make_id'
    | 'model_id'
    | 'model_name'
    | 'model_year'
    | 'battery_capacity'
    | 'charging_connector_type'
    | 'charging_time'
    | 'range_per_charge'
    | 'price_per_minute_ev'
    | 'price_per_km_ev'
    | 'price_per_minute_up_to_100_km_ev'
    | 'price_per_minute_above_100_km_ev'
    | 'pause_price_ev'
    | 'deposit_amount_ev'
    | 'car_type'
    | 'transmission'
    | 'fuel_type'
    | 'identity_country'
    | 'plate_number'
    | 'seats'
    | 'doors'
    | 'car_description'
    | 'charging_included'
    | 'battery_included'
    | 'charging_policy'
    | 'no_branded_title'
    | 'status_doors'
    | 'status_engine'
    | 'status_windows'
    | 'type_connection_geotek'
    | 'status'
    | 'price_per_hour_above_100_km_ev'
    | 'start_price'
    | 'reservation_extension_price'
    | 'reserve_price_ev'
    | 'price_per_hour_ev'
    | 'price_per_hour_up_to_100_km_ev'
  >
> & {
  features?: EvCarProfileFeatures
  admin_comment?: string // For single comment when updating status
}

export type EvCarProfileUpdateableKeys = keyof EvCarProfileUpdateParams
export type EvCarProfileFeaturesUpdateParams = Partial<
  Pick<
    EvCarProfileFeatures,
    'gps' | 'air_conditioner' | 'bluetooth_stereo' | 'fire_extinguisher'
  >
>
